import * as React from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { getAt, getList, pathKey, setAt, type Path } from './draft'
import {
  ObjectListField,
  StringListField,
  TextAreaField,
  TextField,
  type DraftControl,
} from './fields'
import { TableField } from './table-field'
import { ImageField } from './ImageField'
import type { FieldSpec, SectionSpec } from './sections'

/** Render one descriptor at a path. */
function FieldAt({
  control,
  base,
  spec,
}: {
  control: DraftControl
  base: Path
  spec: FieldSpec
}): React.ReactElement {
  const path = [...base, spec.key]
  switch (spec.kind) {
    case 'text':
      return (
        <TextField
          control={control}
          path={path}
          label={spec.label}
          optional={spec.optional ?? false}
          {...(spec.hint === undefined ? {} : { hint: spec.hint })}
          {...(spec.placeholder === undefined ? {} : { placeholder: spec.placeholder })}
        />
      )
    case 'image':
      return (
        <ImageField
          control={control}
          path={path}
          label={spec.label}
          optional={spec.optional ?? false}
          {...(spec.hint === undefined ? {} : { hint: spec.hint })}
        />
      )
    case 'textarea':
      return (
        <TextAreaField
          control={control}
          path={path}
          label={spec.label}
          optional={spec.optional ?? false}
          {...(spec.hint === undefined ? {} : { hint: spec.hint })}
          {...(spec.rows === undefined ? {} : { rows: spec.rows })}
        />
      )
    case 'strings':
      return (
        <StringListField
          control={control}
          path={path}
          label={spec.label}
          itemLabel={spec.itemLabel}
          {...(spec.hint === undefined ? {} : { hint: spec.hint })}
          {...(spec.placeholder === undefined ? {} : { placeholder: spec.placeholder })}
        />
      )
    case 'objects':
      return (
        <ObjectListField
          control={control}
          path={path}
          label={spec.label}
          itemLabel={spec.itemLabel}
          fields={spec.fields}
          {...(spec.hint === undefined ? {} : { hint: spec.hint })}
        />
      )
    case 'group':
      return (
        <Card className="space-y-4 p-3">
          <span className="text-sm font-medium text-ink">{spec.label}</span>
          <FieldList control={control} base={path} specs={spec.fields} />
        </Card>
      )
  }
}

/** Plain fields side by side, lists and groups full width. */
function FieldList({
  control,
  base,
  specs,
}: {
  control: DraftControl
  base: Path
  specs: readonly FieldSpec[]
}): React.ReactElement {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {specs.map((spec) => (
        <div
          key={spec.key}
          className={spec.kind === 'text' || spec.kind === 'image' ? '' : 'sm:col-span-2'}
        >
          <FieldAt control={control} base={base} spec={spec} />
        </div>
      ))}
    </div>
  )
}

/**
 * The pricing section, which is two tables rather than a list of fields.
 *
 * The one real choice here is whether both tables share column headers or each
 * has its own. The contract allows either but not half of one — `columnsHigh`
 * without `columnsLow` leaves a table with no headings — so this is a toggle
 * rather than two independent fields, and switching it rewrites both tables'
 * headers together.
 */
function PricingFields({ control }: { control: DraftControl }): React.ReactElement {
  const shared = getAt(control.draft, ['pricing', 'columnsHigh']) === undefined
  const sharedColumns = getList(control.draft, ['pricing', 'columns'])

  function useShared(): void {
    // Keep the high table's headers as the shared set, so switching does not
    // silently empty a table somebody has just filled in.
    const kept = getList(control.draft, ['pricing', 'columnsHigh'])
    let next = setAt(control.draft, ['pricing', 'columns'], kept.length > 0 ? kept : sharedColumns)
    next = setAt(next, ['pricing', 'columnsHigh'], undefined)
    next = setAt(next, ['pricing', 'columnsLow'], undefined)
    control.onChange(next)
  }

  function useSeparate(): void {
    const kept = sharedColumns.length > 0 ? sharedColumns : ['', '']
    let next = setAt(control.draft, ['pricing', 'columnsHigh'], kept)
    next = setAt(next, ['pricing', 'columnsLow'], [...kept])
    next = setAt(next, ['pricing', 'columns'], undefined)
    control.onChange(next)
  }

  return (
    <div className="space-y-6">
      <Card className="flex flex-wrap items-center gap-2 p-3">
        <span className="text-sm text-ink">
          {shared ? 'Both tables share column headers.' : 'Each table has its own headers.'}
        </span>
        <Button type="button" variant="outline" size="sm" onClick={shared ? useSeparate : useShared}>
          {shared ? 'Give each its own' : 'Share one set'}
        </Button>
      </Card>

      <TableField
        control={control}
        label="Peak season"
        columnsPath={shared ? ['pricing', 'columns'] : ['pricing', 'columnsHigh']}
        rowsPath={['pricing', 'high', 'rows']}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField control={control} path={['pricing', 'high', 'label']} label="Peak label" />
        <TextField control={control} path={['pricing', 'high', 'months']} label="Peak months" />
      </div>

      <TableField
        control={control}
        label="Off-peak season"
        columnsPath={shared ? ['pricing', 'columns'] : ['pricing', 'columnsLow']}
        rowsPath={['pricing', 'low', 'rows']}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField control={control} path={['pricing', 'low', 'label']} label="Off-peak label" />
        <TextField control={control} path={['pricing', 'low', 'months']} label="Off-peak months" />
      </div>
    </div>
  )
}

/** One section of the document, open for editing. */
export function SectionForm({
  control,
  spec,
}: {
  control: DraftControl
  spec: SectionSpec
}): React.ReactElement {
  return (
    <div className="space-y-4">
      <FieldList control={control} base={[spec.key]} specs={spec.fields} />
      {spec.custom === 'pricing' ? <PricingFields control={control} /> : null}
      {/* A rule about the section as a whole — two headings in the hero, a row
          of the wrong width — is reported at the section rather than at any one
          field, so it would otherwise have nowhere to appear. */}
      <SectionLevelProblems control={control} spec={spec} />
    </div>
  )
}

function SectionLevelProblems({
  control,
  spec,
}: {
  control: DraftControl
  spec: SectionSpec
}): React.ReactElement | null {
  const messages = control.problems.byPath.get(pathKey([spec.key]))
  if (messages === undefined || messages.length === 0) return null
  return (
    <ul className="space-y-1">
      {messages.map((message) => (
        <li key={message} className="text-sm text-danger-ink">
          {message}
        </li>
      ))}
    </ul>
  )
}
